import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Layout from "@/components/Layout";
import ManagerDashboard from "@/pages/ManagerDashboard";
import EngineerDashboard from "@/pages/EngineerDashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import BoardPage from "@/pages/BoardPage";
import SprintsPage from "@/pages/SprintsPage";
import TicketsPage from "@/pages/TicketsPage";
import TeamPage from "@/pages/TeamPage";
import PortalsPage from "@/pages/PortalsPage";
import RequestsPage from "@/pages/RequestsPage";
import HrPage from "@/pages/HrPage";
import RcaListPage from "@/pages/RcaListPage";
import RcaDetailPage from "@/pages/RcaDetailPage";
import RcaReportPage from "@/pages/RcaReportPage";
import AccuracyPage from "@/pages/AccuracyPage";
import AccuracyProjectPage from "@/pages/AccuracyProjectPage";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex items-center gap-3 text-slate-500">
        <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        Loading Thinkneural…
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/dashboard" element={<Protected><Layout><DashboardSwitch /></Layout></Protected>} />
          <Route path="/boards/:boardId" element={<Protected><Layout><BoardPage /></Layout></Protected>} />
          <Route path="/sprints" element={<Protected><Layout><SprintsPage /></Layout></Protected>} />
          <Route path="/tickets" element={<Protected><Layout><TicketsPage /></Layout></Protected>} />
          <Route path="/team" element={<Protected><Layout><TeamPage /></Layout></Protected>} />
          <Route path="/portals" element={<Protected><Layout><PortalsPage /></Layout></Protected>} />
          <Route path="/requests" element={<Navigate to="/hr?tab=requests" replace />} />
          <Route path="/hr" element={<Protected><Layout><StaffOnly><HrPage /></StaffOnly></Layout></Protected>} />
          <Route path="/rca" element={<Protected><Layout><StaffOnly><RcaListPage /></StaffOnly></Layout></Protected>} />
          <Route path="/rca/:rcaId/report" element={<Protected><Layout><StaffOnly><RcaReportPage /></StaffOnly></Layout></Protected>} />
          <Route path="/rca/:rcaId" element={<Protected><Layout><StaffOnly><RcaDetailPage /></StaffOnly></Layout></Protected>} />
          <Route path="/accuracy" element={<Protected><Layout><AccuracyPage /></Layout></Protected>} />
          <Route path="/accuracy/:projectId" element={<Protected><Layout><AccuracyProjectPage /></Layout></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function StaffOnly({ children }) {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/dashboard" replace />;
  return children;
}

function DashboardSwitch() {
  const { user } = useAuth();
  if (user?.role === "client") return <ClientDashboard />;
  if (user?.role === "engineer") return <EngineerDashboard />;
  return <ManagerDashboard />;
}

export default App;
